import React, { useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { designSystem } from '@yola/ws-ui';
import { view, blocks, cssGenerator } from '@yola/ws-sdk';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import Trigger from 'src/js/modules/common/components/trigger';
import contextMenu from 'src/js/modules/context-menu';
import segment from 'src/js/modules/analytics/segment';
import createPositionCalculator from 'src/js/modules/control-pane/helpers/create-position-calculator';
import highlighter from 'src/js/modules/highlighter';
import getParentBlockByElement from 'src/js/modules/blocks/helpers/get-parent-block-by-element';
import getBreakpointForCurrentDevice from 'src/js/modules/breakpoint-editing/helpers/get-breakpoint-for-current-device';
import { DESKTOP_ONLY_BREAKPOINT } from 'src/js/modules/breakpoint-editing/constants/breakpoints';
import restoreRange from '../helpers/restore-range';
import { LINE_AND_PARAGRAPH_SPACING_TRIGGER_ID } from '../constants/line-and-paragraph-spacing-trigger-ids';
import getElementTypeOptions from '../helpers/get-element-type-options';
import getTypedElementSelector from '../helpers/get-typed-element-selector';
import findTypedParentElements from '../helpers/find-typed-parent-elements';
import getLineAndParagraphSpacingMenuGroups from '../helpers/get-line-and-paragraph-spacing-menu-groups';
import getAdvancedSelection from '../helpers/get-advanced-selection';

const { Icon } = designSystem;
const {
  trackers: { trackEvent },
  constants: { events },
} = segment;
const { breakpointModifiers } = view;

const isTouchDevice = bowser.tablet || bowser.mobile;

const isContextMenuVisible = () => {
  const { triggerId, visibility } = contextMenu.accessors.getMenu();

  return triggerId === LINE_AND_PARAGRAPH_SPACING_TRIGGER_ID && visibility;
};

function LineAndParagraphSpacingTrigger({ elementId, triggerId, getContext }) {
  const triggerRef = useRef();
  const dispatch = useDispatch();
  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;
  const activeBreakpoint = isBreakpointEditingEnabled
    ? getBreakpointForCurrentDevice(breakpointModifiers.ONLY)
    : DESKTOP_ONLY_BREAKPOINT;

  const liveDocument = view.accessors.getLiveDocument();
  const element = view.accessors.getLiveElement(elementId);
  const parentBlock = getParentBlockByElement(element);

  const selection = getAdvancedSelection();
  const elementTypeOptions = getElementTypeOptions(elementId);
  const typedElementSelector = getTypedElementSelector(elementTypeOptions);
  const focusedElements = findTypedParentElements(typedElementSelector, selection);
  const blockId = blocks.accessors.getBlockIdByElement(parentBlock);
  const blockVariationId = blocks.accessors.getVariationIdByElement(parentBlock);

  const handleMouseDown = useCallback((e) => {
    // We need this to stop the context menu from hiding
    // This gives us the ability to control the display/hiding of the context menu
    e.stopPropagation();
  }, []);

  const handleItemClick = async (id) => {
    contextMenu.operations.hideContextMenu();

    trackEvent(events.TEXT_EDITING_LINE_AND_PARAGRAPH_OPTION_CLICKED, {
      blockId,
      blockVariationId,
      triggerId: id,
    });

    const css = await cssGenerator.operations.generateCssFromHtml(
      liveDocument.documentElement.innerHTML
    );

    dispatch(view.actions.updateAtomicCSS(css));
    restoreRange(selection, elementId);

    highlighter.operations.show([element], {
      forceUpdate: true,
      labelGap: 'medium',
    });
  };

  const handleClick = () => {
    if (!isTouchDevice) {
      restoreRange(selection, elementId, true);
    }

    if (isContextMenuVisible()) {
      contextMenu.operations.hideContextMenu();
      return;
    }

    const { controlPane } = getContext();
    const { position } = controlPane;

    const menuGroups = getLineAndParagraphSpacingMenuGroups(
      element,
      focusedElements,
      activeBreakpoint
    ).map(({ name, items }) => ({
      name,
      element,
      items: items.map((item) => ({
        ...item,
        onClick: (event) => {
          event.stopPropagation();
          item.onClick();
          handleItemClick(item.id);
        },
      })),
    }));

    const { triggerRef: ref } = triggerRef.current;

    contextMenu.operations.showContextMenu({
      position,
      groups: menuGroups,
      triggerId,
      calculatePosition: createPositionCalculator(controlPane, ref),
    });
  };

  return (
    <Trigger id={triggerId} ref={triggerRef} onMouseDown={handleMouseDown} onClick={handleClick}>
      <Icon glyph="line-height" size="24" strokeWidth="1.3" />
    </Trigger>
  );
}

LineAndParagraphSpacingTrigger.propTypes = {
  elementId: PropTypes.string.isRequired,
  triggerId: PropTypes.string.isRequired,
  getContext: PropTypes.func.isRequired,
};

export default LineAndParagraphSpacingTrigger;
